// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {UserIconEl} from 'common/widgets/user-icon';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {Annotation} from 'common/report/queries/app-api/annotations-client';
import {formatText} from 'common/util/format-text';
import {IconSizeType} from 'common/constants';

import type {ViewAnnotationsList} from './index';

const AnnotationRow = ({
  isEditingThisAnnotation,
  isPreview,
  canWriteAnnotations,
  annotationDescriptionDraft,
  isDeletingThisAnnotation,
  annotation,
  viewAnnotationsList,
}: {
  isEditingThisAnnotation: boolean;
  isPreview: boolean;
  canWriteAnnotations: boolean;
  annotationDescriptionDraft: any;
  isDeletingThisAnnotation: boolean;
  annotation: any;
  viewAnnotationsList: ViewAnnotationsList;
}) => (
  <div
    sel='.annotations-viewer-annotation-row'
    class={{
      'annotations-viewer-editing': isEditingThisAnnotation,
      'annotations-viewer-is-preview': isPreview,
    }}
    attrs={{
      elref: `annotationRow`,
    }}
  >
    {!isEditingThisAnnotation ? (
      <div sel='.annotations-viewer-annotation-icon'>
        <UserIconEl
          attrs={{
            name: annotation.user && `${annotation.user.first_name} ${annotation.user.last_name}`,
            id: annotation.user && annotation.user.id,
            'disable-tooltip': true,
            size: IconSizeType.Medium,
          }}
        />
      </div>
    ) : null}
    <div sel='.annotations-viewer-annotation-content'>
      <div sel='.annotations-viewer-annotation-header'>
        <div sel='.annotations-viewer-annotation-meta'>
          {annotation.user ? (
            <div sel='.annotations-viewer-annotation-user-label'>
              {`${annotation.user.first_name} ${annotation.user.last_name && annotation.user.last_name[0]}`}
            </div>
          ) : null}
          <div sel='.annotations-viewer-annotation-date-label'>
            {viewAnnotationsList.helpers.formatDate(annotation.highchartsDate)}
          </div>
        </div>
        {canWriteAnnotations && !isEditingThisAnnotation && !isDeletingThisAnnotation ? (
          <div sel='.annotations-viewer-annotation-actions'>
            <ButtonEl
              sel='.annotations-viewer-annotation-edit-button'
              attrs={{icon: `edit`, theme: ButtonTheme.Tertiary, elref: `annotationEdit`}}
              on={{click: () => viewAnnotationsList.helpers.edit(annotation)}}
            />
            <ButtonEl
              sel='.annotations-viewer-annotation-delete-button'
              attrs={{
                icon: `trashcan`,
                theme: ButtonTheme.Tertiary,
                type: ButtonType.Destructive,
                elref: `annotationDelete`,
              }}
              on={{click: () => viewAnnotationsList.helpers.delete(annotation)}}
            />
          </div>
        ) : null}
      </div>
      {isEditingThisAnnotation ? (
        <div sel='.annotations-viewer-annotation-edit-mode'>
          <textarea
            on={{input: viewAnnotationsList.helpers.handleDescriptionChanged}}
            attrs={{elref: `annotationTextArea`, maxlength: 512}}
          >
            {annotation.description}
          </textarea>
          <div sel='.annotations-viewer-annotation-edit-actions'>
            <ButtonEl attrs={{theme: ButtonTheme.Tertiary}} on={{click: viewAnnotationsList.helpers.editCanceled}}>
              Cancel
            </ButtonEl>
            <ButtonEl
              attrs={{theme: ButtonTheme.Primary, disabled: !annotationDescriptionDraft, elref: `annotationSave`}}
              on={{click: viewAnnotationsList.helpers.save}}
            >
              Save
            </ButtonEl>
          </div>
        </div>
      ) : (
        <div sel='.annotations-viewer-annotation-description' class={{'annotations-viewer-is-preview': isPreview}}>
          {formatText(annotation.description)}
        </div>
      )}
    </div>
    {isDeletingThisAnnotation ? (
      <div sel='.annotations-viewer-confirm-delete-container'>
        <div sel='.annotations-viewer-confirm-delete-buttons'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary}} on={{click: viewAnnotationsList.helpers.deleteCanceled}}>
            Cancel
          </ButtonEl>
          <ButtonEl
            attrs={{theme: ButtonTheme.Primary, type: ButtonType.Destructive, elref: `annotationDeleteConfirm`}}
            on={{click: viewAnnotationsList.helpers.deleteConfirmed}}
          >
            Delete
          </ButtonEl>
        </div>
      </div>
    ) : null}
  </div>
);

export function template(this: ViewAnnotationsList) {
  const activeAnnotationButton = this.params.activeAnnotation;
  const isPreview = this.params.type === this.helpers.AnnotationViewType.PREVIEW;
  const {activeAnnotationGroup, numAnnotations} = this.helpers.getActiveAnnotationGroup();
  const editingAnnotation = this.params.editingAnnotation;
  const deletingAnnotation = this.params.deletingAnnotation;

  return (
    <div sel='.annotations-viewer-container'>
      {activeAnnotationButton ? (
        <>
          <div sel='.annotations-viewer-header' class={{'annotations-viewer-is-preview': isPreview}}>
            <div sel='.annotations-viewer-header-date'>{activeAnnotationButton.formattedDate}</div>
            <div sel='.annotations-viewer-header-count'>
              {numAnnotations === 1 ? `${numAnnotations} Annotation` : `${numAnnotations} Annotations`}
            </div>
          </div>
          <div sel='.annotations-viewer-annotations-list'>
            {activeAnnotationGroup.map((annotation: Annotation) => {
              return (
                <AnnotationRow
                  isEditingThisAnnotation={!!(editingAnnotation && editingAnnotation.id === annotation.id)}
                  isPreview={isPreview}
                  canWriteAnnotations={this.params.canWriteAnnotations}
                  annotationDescriptionDraft={this.params.annotationDescriptionDraft}
                  isDeletingThisAnnotation={!!(deletingAnnotation && deletingAnnotation.id === annotation.id)}
                  annotation={annotation}
                  viewAnnotationsList={this}
                />
              );
            })}
          </div>
          <div sel='.annotations-viewer-footer' class={{'annotations-viewer-is-preview': isPreview}}>
            {isPreview ? (
              numAnnotations > 2 ? (
                `+ ${numAnnotations - 2} more...`
              ) : null
            ) : (
              <ButtonEl on={{click: this.helpers.createAnnotation}}>Add Annotation</ButtonEl>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
